import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../../components/login/UserContext";
import { useScrollLock } from "../../hooks/useScrollLock";
import { onOpenSupport } from "./supportBus";
import { submitSupportTicket } from "./supportApi";
import styles from "./support.module.scss";

const CATEGORIES = ["General", "Billing", "Account", "Bug", "Other"];

/**
 * Support form for logged-in members. Posts to /api/v1/support/tickets, which
 * alerts the admin inbox by email. The admin replies from /admin/support by
 * email and/or in-app notification — there is no AI auto-reply.
 *
 * No launcher of its own: help is a nav icon now (the desktop rail, the mobile
 * bottom bar), and every entry point — those, the account drawer, "My
 * requests" — opens this sheet through {@link onOpenSupport}.
 */
const SupportWidget: React.FC = () => {
  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useScrollLock(open);

  useEffect(() => onOpenSupport(() => setOpen(true)), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const reset = useCallback(() => {
    setSubject("");
    setCategory(CATEGORIES[0]);
    setMessage("");
    setError(null);
    setSent(false);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setTimeout(reset, 220);
  }, [reset]);

  const goToMyRequests = useCallback(() => {
    setOpen(false);
    setTimeout(reset, 220);
    navigate("/support");
  }, [navigate, reset]);

  const submit = useCallback(async () => {
    if (!message.trim()) {
      setError("Please enter a message.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await submitSupportTicket({
        subject: subject.trim() || undefined,
        category,
        bodyText: message.trim(),
      });
      setSent(true);
    } catch (e: any) {
      const status = e?.response?.status;
      setError(
        status === 429
          ? "You already have several open requests — we'll get back to you shortly."
          : "Something went wrong sending your message. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  }, [subject, category, message]);

  const isAuthed = !!user && user.id !== 0;
  if (!isAuthed || location.pathname.startsWith("/admin")) {
    return null;
  }

  return (
    <>
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Contact support"
          className={styles.overlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div className={styles.panel}>
            <span className={styles.panelGlass} aria-hidden="true" />

            <div className={styles.panelScroll}>
              <div className={styles.panelHeader}>
                <div>
                  <div className={styles.eyebrow}>Support desk</div>
                  <h2 className={styles.title}>Contact support</h2>
                  <p className={styles.copy}>
                    A real person will get back to you by email or in-app.
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={close}
                  className={styles.closeBtn}
                >
                  ×
                </button>
              </div>

              {sent ? (
                <>
                  <div className={styles.successTitle}>Message sent — we've got it.</div>
                  <p className={styles.successCopy}>
                    We'll reply to your account email and drop a notification in
                    your inbox. Thanks for reaching out.
                  </p>
                  <div className={styles.actions}>
                    <button type="button" className={styles.primaryBtn} onClick={close}>
                      Done
                    </button>
                    <button type="button" className={styles.linkBtn} onClick={goToMyRequests}>
                      View my requests →
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>Subject (optional)</span>
                    <input
                      className={styles.input}
                      value={subject}
                      maxLength={160}
                      placeholder="What's this about?"
                      onChange={(e) => setSubject(e.target.value)}
                    />
                  </label>

                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>Category</span>
                    <select
                      className={styles.select}
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>How can we help?</span>
                    <textarea
                      className={styles.textarea}
                      value={message}
                      maxLength={4000}
                      placeholder="Tell us what's going on…"
                      onChange={(e) => setMessage(e.target.value)}
                    />
                  </label>

                  {error && <div className={styles.error}>{error}</div>}

                  <div className={styles.actions}>
                    <button
                      type="button"
                      className={styles.primaryBtn}
                      disabled={submitting || !message.trim()}
                      onClick={submit}
                    >
                      {submitting ? "Sending…" : "Send message"}
                    </button>
                    <button type="button" className={styles.linkBtn} onClick={goToMyRequests}>
                      View my past requests →
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportWidget;
